import { Component, computed, inject, Inject, OnDestroy, ViewChild } from '@angular/core';
import { ReactiveFormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { NZ_MODAL_DATA, NzModalModule, NzModalRef, NzModalService } from 'ng-zorro-antd/modal';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import {
  catchError,
  EMPTY,
  finalize,
  map,
  Observable,
  of,
  ReplaySubject,
  switchMap,
  takeUntil,
  tap,
  throwError
} from 'rxjs';
import { IAddReferralResponse, IInvitationConflictPayload, IReferral, IUserType } from 'core/types/referral.interface';
import { InvitationsService } from 'core';
import { NzButtonModule } from 'ng-zorro-antd/button';

import { NzRadioModule } from 'ng-zorro-antd/radio';
import { VerifyReferralModalComponent } from './verify-referral-modal/verify-referral-modal.component';
import { NetworkError } from '../../../shared/models/network-error';
import { ErrorCodes } from '../../../shared/types/error-codes.enum';
import { AddReferralStatuses } from './core/types/add-referral-statuses.enum';
import { IValidateReferralStatusData } from '../../../shared/types/validation-status-modal-data.interface';
import { KorviuButtonComponent } from '../../../shared/components/korviu-button/korviu-button.component';
import { UserTypes } from '../../../shared/types/user.model';
import { GanasocioUserFormComponent } from './ganasocio-user-form/ganasocio-user-form.component';
import { DefaultUsersFormComponent } from './default-users-form/default-users-form.component';
import { AuthService } from 'core/services/auth.service';
import { CustomMessageService } from 'core/services/custom-message.service';
import { EditConfirmationModalComponent } from './edit-confirmation-modal/edit-confirmation-modal.component';
import { buildInvitationSendPayload } from 'shared/utils/invitation-send-payload.utils';
import { ReferralUpdatesService } from '../services/referral-updates.service';
import {
  buildWhatsappSharePayload,
  ensureWhatsappShareLogged,
  getWhatsappMessageFromUrl,
  withWhatsappMessage
} from 'shared/utils/whatsapp-share.utils';
import {
  GanasocioInvitationConflictAction,
  GanasocioInvitationConflictModalComponent,
  GanasocioInvitationConflictVariant
} from './ganasocio-invitation-conflict-modal/ganasocio-invitation-conflict-modal.component';

const CONTACT_METHOD_VALIDATION_ERROR_CODES = new Set([
  'ERR_EMAIL_REGISTERED',
  'ERR_PHONE_REGISTERED',
  'ERR_DUPLICATE'
]);

@Component({
  selector: 'referral-modal',
  standalone: true,
  imports: [
    TranslateModule,
    ReactiveFormsModule,
    NzModalModule,
    NzRadioModule,
    NzButtonModule,
    KorviuButtonComponent,
    GanasocioUserFormComponent,
    DefaultUsersFormComponent
  ],
  templateUrl: './referral-modal.component.html',
  styleUrl: './referral-modal.component.scss',
  providers: [InvitationsService]
})
export class ReferralModalComponent implements OnDestroy {
  @ViewChild('ganasocioUserForm') ganasocioUserForm: GanasocioUserFormComponent;
  @ViewChild('defaultUsersForm') defaultUsersForm: DefaultUsersFormComponent;

  readonly #referralUpdates = inject(ReferralUpdatesService);

  public UserTypes: typeof UserTypes = UserTypes;
  public isLoading: boolean = false;

  public get formIsValid(): boolean {
    return this.defaultUsersForm?.isValid || this.ganasocioUserForm?.isValid;
  }

  public get isGanasocios() {
    return this.modalData?.userType === UserTypes.PR;
  }

  userType = computed<UserTypes>(() => this.modalData?.userType);

  private _onDestroy$: ReplaySubject<void>;

  constructor(
    @Inject(NZ_MODAL_DATA) public modalData: { userType: UserTypes; isUpdate: boolean; prefillData: IReferral },
    public _modal: NzModalRef,
    private _modalService: NzModalService,
    private _invitationsService: InvitationsService,
    private _authService: AuthService,
    private _messageService: CustomMessageService,
    private _translateService: TranslateService,
    private _router: Router
  ) {
    this._onDestroy$ = new ReplaySubject<void>(1);
  }

  public ngOnDestroy(): void {
    this._onDestroy$.next();
    this._onDestroy$.complete();
  }

  public onCancel(): void {
    this._modal.destroy();
  }

  public onShareClick(): void {
    this.userType() === UserTypes.PR ||
    this.userType() === UserTypes.AY ||
    this.userType() === UserTypes.CO ||
    this.userType() === UserTypes.RE
      ? this.ganasocioUserForm.validateAndSubmit()
      : this.defaultUsersForm.validateAndSubmit();
  }

  public onFormSubmit(referral: IReferral): void {
    if (this.modalData?.isUpdate) {
      this._openEditConfirmationModal(referral);
      this._modal.destroy();
    } else {
      this.addReferral(referral);
    }
  }

  public addReferral(referral: IReferral): void {
    this.isLoading = true;
    const invitationPayload = buildInvitationSendPayload(referral, this.userType());
    let referralData: any = {
      phone_code: referral?.country_code,
      phone_number: referral?.phone_number
    };
    this._invitationsService
      .addReferral(invitationPayload, this.userType(), this.modalData?.isUpdate)
      .pipe(
        switchMap((response: IAddReferralResponse) => {
          const invitationResponse = this._unwrapAddReferralResponse(response);
          const { id: referralId, type: referralType } = invitationResponse;
          referralData.referral_id = referralId;
          referralData.referral_type = referralType;

          return this._shareWhatsappInvitation(invitationResponse, referral.message_template).pipe(
            switchMap(() => this._authService.getMe(true).pipe(catchError(() => of(null)))),
            tap(() => {
              this.#referralUpdates.notifyChanged();
              this._openVerifyReferralModal(this._getReferralStatus(invitationResponse), referralData);
            })
          );
        }),
        finalize(() => {
          this.isLoading = false;
        }),
        catchError((error: NetworkError) => {
          const messageCode = error?.errorPayload?.messageCode;

          if (messageCode === 'ERR_ALREADY_REGISTERED' || (this.isGanasocios && messageCode === 'ERR_DUPLICATE')) {
            this._openInvitationConflict(error.errorPayload as IInvitationConflictPayload);
            return EMPTY;
          } else if (error?.code === ErrorCodes.ERR_PHONE_EXISTS) {
            this._openVerifyReferralModal(AddReferralStatuses.INVALIDPHONE, referralData);
          } else if (messageCode === 'ERR_INVITATION_ALREADY_SENT') {
            this._openVerifyReferralModal(AddReferralStatuses.INVITATION_ALREADY_SENT, referralData);
          } else if (CONTACT_METHOD_VALIDATION_ERROR_CODES.has(messageCode)) {
            this._showInvitationError(messageCode);
            return EMPTY;
          } else {
            this._showInvitationError('UNKNOWN_ERROR');
            return EMPTY;
          }
          return of(null);
        }),
        takeUntil(this._onDestroy$)
      )
      .subscribe(() => {
        this._modal.close(true);
      });
  }

  private _openInvitationConflict(payload: IInvitationConflictPayload): void {
    const details = payload.data ?? payload;
    const invitedUserType = this._resolveInvitedUserType(details.invited_user_type);
    const variant: GanasocioInvitationConflictVariant =
      payload.messageCode === 'ERR_ALREADY_REGISTERED'
        ? 'already-registered'
        : invitedUserType.isGanasocio
          ? 'duplicate-ganasocio'
          : 'duplicate-other-type';

    this._modalService
      .create({
        nzContent: GanasocioInvitationConflictModalComponent,
        nzData: {
          variant,
          invitedUserTypeName: invitedUserType.name
        },
        nzCentered: true,
        nzClosable: false,
        nzFooter: null,
        nzWidth: 'auto',
        nzBodyStyle: { padding: '0', background: 'transparent' },
        nzClassName: 'ganasocio-invitation-conflict-modal-dialog'
      })
      .afterClose.pipe(takeUntil(this._onDestroy$))
      .subscribe((action?: GanasocioInvitationConflictAction) => {
        if (action === 'add-new-referral') {
          this.ganasocioUserForm?.referralForm.reset();
          this.defaultUsersForm?.referralForm.reset({
            type: 'whatsapp',
            language: 'es',
            live_in_us: true,
            contact_name: ' '
          });
        } else if (action === 'see-invitation' || action === 'cancel-invitation') {
          if (!details.invitation_id) {
            this._showInvitationError('UNKNOWN_ERROR');
            return;
          }

          if (action === 'see-invitation') {
            this._modal.destroy();
            void this._router.navigate(['/workspace/my-referrals/referral', details.invitation_id]);
          } else {
            this._cancelInvitation(details.invitation_id);
          }
        }
      });
  }

  private _cancelInvitation(invitationId: number): void {
    this.isLoading = true;
    this._invitationsService
      .deleteInvitation(invitationId)
      .pipe(
        tap(() => this.#referralUpdates.notifyChanged()),
        switchMap(() => this._authService.getMe(true).pipe(catchError(() => of(null)))),
        finalize(() => (this.isLoading = false)),
        takeUntil(this._onDestroy$)
      )
      .subscribe({
        next: () => {
          this._messageService.success(
            this._translateService.instant('buildYourNetwork.ganasocioConflict.invitationCancelled')
          );
        },
        error: () => {
          this._showInvitationError('UNKNOWN_ERROR');
        }
      });
  }

  private _resolveInvitedUserType(invitedUserType?: IUserType | string): { isGanasocio: boolean; name: string } {
    const code = (typeof invitedUserType === 'string' ? invitedUserType : invitedUserType?.code)?.trim();
    const backendName = (typeof invitedUserType === 'string' ? undefined : invitedUserType?.name)?.trim();
    const normalizedCode = code?.toUpperCase() || '';
    const normalizedName = backendName?.toUpperCase() || '';

    return {
      isGanasocio: [UserTypes.PR, 'PR', 'GANASOCIO'].includes(normalizedCode) || normalizedName === 'GANASOCIO',
      name:
        this._getUserTypeName(normalizedCode) ||
        this._getUserTypeName(normalizedName) ||
        backendName ||
        this._translateService.instant('userTypes.member')
    };
  }

  private _getUserTypeName(userTypeCode: string): string | undefined {
    const userTypeNames: Record<string, string> = {
      [UserTypes.PR]: 'userTypes.ganasocio',
      PR: 'userTypes.ganasocio',
      GANASOCIO: 'userTypes.ganasocio',
      [UserTypes.AY]: 'userTypes.ganaliado',
      [UserTypes.CO]: 'userTypes.ganagente',
      [UserTypes.RE]: 'userTypes.ganaquipo',
      [UserTypes.PA]: 'userTypes.ganatron',
      [UserTypes.MU]: 'userTypes.member',
      [UserTypes.MX]: 'userTypes.member',
      [UserTypes.SD]: 'userTypes.member',
      MEMBER: 'userTypes.member'
    };

    const translationKey = userTypeNames[userTypeCode];
    return translationKey ? this._translateService.instant(translationKey) : undefined;
  }

  private _openEditConfirmationModal(referral: IReferral): void {
    this._modalService.create({
      nzContent: EditConfirmationModalComponent,
      nzData: { referral: { ...referral }, userType: this.userType() },
      nzCentered: true,
      nzClosable: false,
      nzWidth: 554,
      nzWrapClassName: 'modal-wrap'
    });
  }

  private _unwrapAddReferralResponse(response: IAddReferralResponse): IAddReferralResponse {
    return response?.data || response;
  }

  private _getReferralStatus(response: IAddReferralResponse): AddReferralStatuses {
    if (response?.approval_required || response?.status?.toLowerCase() === 'pending') {
      return AddReferralStatuses.PENDING_APPROVAL;
    }

    if (response?.whatsapp_url) {
      return AddReferralStatuses.WHATSAPP_READY;
    }

    return AddReferralStatuses.INVITED;
  }

  private _shareWhatsappInvitation(response: IAddReferralResponse, customMessage?: string): Observable<unknown> {
    if (!response?.id || response?.approval_required || !response?.whatsapp_url) {
      return of(null);
    }

    const message = customMessage?.trim() || response.message || getWhatsappMessageFromUrl(response.whatsapp_url);
    if (!message) {
      window.open(response.whatsapp_url, '_blank');
      return throwError(() => new Error('WhatsApp share message is missing'));
    }

    window.open(withWhatsappMessage(response.whatsapp_url, message), '_blank');

    return this._invitationsService
      .logWhatsappShare(response.id, buildWhatsappSharePayload(response.id, message))
      .pipe(map(ensureWhatsappShareLogged));
  }

  private _showInvitationError(messageCode: string): void {
    this._messageService.error(this._translateService.instant(`errors.${messageCode}`));
  }

  private _openVerifyReferralModal(status: AddReferralStatuses, referralData?: any): void {
    this._modalService.create({
      nzContent: VerifyReferralModalComponent,
      nzData: {
        status,
        referralData,
        userType: this.userType()
      } as IValidateReferralStatusData,
      nzCentered: true,
      nzClosable: false,
      nzWidth: 554,
      nzWrapClassName: 'modal-wrap'
    });
  }
}
